import { LightningElement, api, wire } from "lwc";
import classNames from "classnames";

import { assignedSlotNames } from "common/slot";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import {
  MediaQueryAdapter,
  MediaQueryAdapterValue,
  MEDIA_QUERIES,
} from "common/mediaQueryAdapter";
import { EffectAdapter } from "common/effectAdapter";
import { BooleanAttr, attrToBool } from "common/helpers";

import * as locales from "./locales";
import type { Variant } from "tds/button";
import { DEFAULT_SPRITE, Sprite } from "tds/icon";
import { Config } from "tds/sanitizedHtml";

export default class extends LightningElement {
  @api heading: string | null = null;
  @api headingImageSrc: string | null = null;
  @api headingImageAltText: string | null = null;
  @api headingImageVariant: "circle" | null = null;
  @api headingIconSprite: Sprite = DEFAULT_SPRITE;
  @api headingIconSymbol: string | null = null;
  @api subHeading: string | null = null;
  @api description: string | null = null;
  @api descriptionHtml: string | null = null;
  @api backButtonHref: string | null = null;
  @api searchLabel: string | null = null;
  @api searchValue: string | null = null;
  @api searchDisabled: BooleanAttr = false;
  @api searchBusy: BooleanAttr = false;
  @api primaryActionLabel: string | null = null;
  @api primaryActionIconSymbol: string | null = null;
  @api primaryActionIconSprite: Sprite = DEFAULT_SPRITE;
  @api primaryActionVariant: Variant = "brand";
  @api pretitle: string | null = null;
  @api pretitleHtml: string | null = null;
  @api pretitleHtmlConfig: Config | null = null;
  @api minSearchChars: number | null = null;

  private isStacked: boolean = false;
  private isCollapsible: boolean = true;
  private isCollapseDisabled: boolean = false;
  private closed: boolean = false;
  private slots: string = "";
  private height: number | null = null;

  @api
  get collapsible() {
    return this.isCollapsible && this.slots.includes("filters");
  }

  set collapsible(newValue: BooleanAttr) {
    this.isCollapsible = attrToBool(newValue);
  }

  @api
  get open() {
    return !this.closed;
  }

  set open(newValue: BooleanAttr) {
    this.closed = !attrToBool(newValue);
  }

  @api
  get collapseDisabled() {
    return this.isCollapseDisabled;
  }

  set collapseDisabled(newValue: BooleanAttr) {
    this.isCollapseDisabled = attrToBool(newValue);
  }

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = null!;

  @wire(MediaQueryAdapter, { media: MEDIA_QUERIES.md })
  private onMatch({ matches }: MediaQueryAdapterValue) {
    this.isStacked = matches;
  }

  private get hasPretitle() {
    return !!this.pretitle || !!this.pretitleHtml;
  }

  private get hasSearch() {
    return !!this.searchLabel || !!this.searchValue;
  }

  private get hasHeader() {
    return (
      this.hasSearch ||
      this.primaryActionLabel ||
      this.heading ||
      this.subHeading ||
      this.description ||
      this.descriptionHtml
    );
  }

  private get isSearchDisabled() {
    return attrToBool(this.searchDisabled);
  }

  private get isSearchBusy() {
    return attrToBool(this.searchBusy);
  }

  private get headingImageClass() {
    return classNames(
      "heading__image",
      this.headingImageVariant && `heading__image--${this.headingImageVariant}`
    );
  }

  private get filterSection(): HTMLElement {
    return this.template.querySelector(".filters")!;
  }

  @wire(EffectAdapter, { closed: "$closed" })
  private onFilterData() {
    if (this.closed && this.filterSection) {
      this.collapseFiltersNoTransition(this.filterSection);
    }
    if (!this.closed && this.filterSection) {
      this.expandFilters(this.filterSection);
    }
  }

  private handleSlotChange() {
    this.slots = assignedSlotNames(this.template).join(" ");
  }

  private handleToggleFilters(e: MouseEvent) {
    e.preventDefault();

    if (this.isCollapseDisabled) return;
    this.open = !this.open;

    if (this.open) {
      this.expandFilters(this.filterSection);
      this.dispatchEvent(new CustomEvent("requestopen"));
    } else {
      this.collapseFilters(this.filterSection);
      this.dispatchEvent(new CustomEvent("requestclose"));
    }
  }

  private handlePrimaryActionClick(e: MouseEvent) {
    e.preventDefault();
    this.dispatchEvent(new CustomEvent("primaryaction"));
  }

  private handleSearch(e: CustomEvent) {
    this.dispatchEvent(new CustomEvent("search", e));
  }

  private collapseFilters(element: HTMLElement) {
    let sectionHeight = element.offsetHeight;
    this.height = element.offsetHeight;

    // temporarily disable all css transitions
    let elementTransition = element.style.transition;
    element.style.transition = "";

    // explicitly set the element's height to its current pixel height, so we
    // aren't transitioning out of 'auto'
    requestAnimationFrame(() => {
      element.style.height = sectionHeight + "px";
      element.style.transition = elementTransition;

      // on the next frame have the element transition to height: 0
      requestAnimationFrame(() => {
        element.style.height = 0 + "px";
      });
    });

    element.setAttribute("aria-hidden", "true");
  }

  private collapseFiltersNoTransition(element: HTMLElement) {
    this.height = element.offsetHeight;

    let elementTransition = element.style.transition;
    element.style.transition = "";

    // Seems to only collapse properly on future clicks when wrapped. Causes brief flicker
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        element.style.height = 0 + "px";
        element.style.transition = elementTransition;
      });
    });
  }

  private expandFilters(element: HTMLElement) {
    element.style.height = this.height + "px";
    // when the next css transition finishes
    element.addEventListener("transitionend", function resetHeight() {
      // remove event listener so it only gets triggered once
      element.removeEventListener("transitionend", resetHeight);
      element.style.height = "auto";
    });
    element.setAttribute("aria-hidden", "false");
  }
}
